import { useEffect, useRef, useState } from 'react';

/**
 * A small "Manage" menu for a table row, so a row can offer several actions without a
 * crowd of buttons. items: [{ label, onClick, danger?, hidden? }]. Closes on a click
 * elsewhere, on Escape (focus goes back to the button) and after a choice.
 *
 * The list is position: fixed, placed from the button, because tables sit in a
 * horizontally scrolling wrapper that would otherwise clip it. It opens upwards when
 * there's no room below, and closes if the page scrolls.
 */
export default function RowMenu({ label = 'Manage', items, ariaLabel }) {
  const [open, setOpen] = useState(false);
  const [place, setPlace] = useState(null);
  const wrap = useRef(null);
  const button = useRef(null);
  const shown = items.filter((i) => !i.hidden);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => { if (!wrap.current?.contains(e.target)) setOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') { setOpen(false); button.current?.focus(); } };
    const close = () => setOpen(false);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    window.addEventListener('scroll', close, true);
    window.addEventListener('resize', close);
    wrap.current?.querySelector('[role="menuitem"]')?.focus();
    return () => {
      document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey);
      window.removeEventListener('scroll', close, true); window.removeEventListener('resize', close);
    };
  }, [open]);

  const toggle = () => {
    if (open) { setOpen(false); return; }
    const r = button.current.getBoundingClientRect();
    const height = shown.length * 37 + 10;
    const up = window.innerHeight - r.bottom < height + 8 && r.top > height + 8;
    setPlace({ right: Math.max(8, window.innerWidth - r.right), ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }) });
    setOpen(true);
  };

  const move = (e) => {
    if (!['ArrowDown', 'ArrowUp'].includes(e.key)) return;
    e.preventDefault();
    const all = [...wrap.current.querySelectorAll('[role="menuitem"]')];
    const i = all.indexOf(document.activeElement);
    all[(i + (e.key === 'ArrowDown' ? 1 : all.length - 1)) % all.length]?.focus();
  };

  return (
    <div className="row-menu" ref={wrap}>
      <button ref={button} type="button" className="btn btn-secondary btn-small" aria-haspopup="menu" aria-expanded={open}
        aria-label={ariaLabel} onClick={toggle}>
        {label} <span aria-hidden="true">▾</span>
      </button>
      {open && (
        <div className="row-menu-list" role="menu" style={place} onKeyDown={move}>
          {shown.map((i) => (
            <button key={i.label} type="button" role="menuitem" className={`row-menu-item${i.danger ? ' is-danger' : ''}`}
              onClick={() => { setOpen(false); i.onClick(); }}>{i.label}</button>
          ))}
        </div>
      )}
    </div>
  );
}
